import React from "react";
import { motion } from "framer-motion";

/**
 * Abstract AI tutor presence — soft glowing orb.
 * No face, no eyes, no mouth. Pure gradients + pulse rings + light particles.
 *
 * Brand: light aqua (default) / warm peach (listening).
 */
export const Mascot = ({ state = "idle", size = "md" }) => {
    const dim =
        size === "lg" ? 360 : size === "sm" ? 110 : size === "xs" ? 64 : 220;

    const coreVariants = {
        idle: { scale: [1, 1.04, 1], opacity: 1 },
        speaking: { scale: [1, 1.09, 1, 1.06, 1], opacity: 1 },
        listening: { scale: [1, 0.95, 1], opacity: 0.95 },
        thinking: { scale: [1, 1.02, 1], opacity: 1 },
        explaining: { scale: [1, 1.06, 1], opacity: 1 },
        video: { scale: 1, opacity: 0.7 },
    };
    const coreTransition = {
        idle: { duration: 4, repeat: Infinity, ease: "easeInOut" },
        speaking: { duration: 1.6, repeat: Infinity, ease: "easeInOut" },
        listening: { duration: 2.6, repeat: Infinity, ease: "easeInOut" },
        thinking: { duration: 3, repeat: Infinity, ease: "easeInOut" },
        explaining: { duration: 2, repeat: Infinity, ease: "easeInOut" },
        video: { duration: 0.4 },
    };

    const halo = { rotate: state === "thinking" ? 360 : 0 };
    const isWarm = state === "listening";

    return (
        <div
            className="relative"
            style={{ width: dim, height: dim }}
            data-testid="student-ai-mascot"
            data-mascot-state={state}
        >
            {(state === "speaking" || state === "explaining") &&
                [0, 0.6, 1.2].map((delay, i) => (
                    <motion.div
                        key={i}
                        className="absolute inset-0 rounded-full"
                        style={{
                            border: isWarm
                                ? "1.5px solid rgba(255,154,120,0.55)"
                                : "1.5px solid rgba(91,194,204,0.5)",
                        }}
                        initial={{ scale: 0.85, opacity: 0.6 }}
                        animate={{ scale: 1.55, opacity: 0 }}
                        transition={{
                            duration: 2.4,
                            repeat: Infinity,
                            delay,
                            ease: "easeOut",
                        }}
                    />
                ))}

            {state === "listening" &&
                [0, 0.8].map((delay, i) => (
                    <motion.div
                        key={i}
                        className="absolute inset-0 rounded-full"
                        style={{ border: "1.5px solid rgba(255,154,120,0.55)" }}
                        initial={{ scale: 1.4, opacity: 0 }}
                        animate={{ scale: 0.95, opacity: 0.6 }}
                        transition={{
                            duration: 2.4,
                            repeat: Infinity,
                            delay,
                            ease: "easeOut",
                        }}
                    />
                ))}

            <motion.div
                className="absolute -inset-3 rounded-full"
                style={{
                    background:
                        "conic-gradient(from 0deg, rgba(184,232,232,0), rgba(184,232,232,0.65), rgba(255,207,175,0.45), rgba(184,232,232,0))",
                    filter: "blur(18px)",
                    opacity: state === "thinking" ? 0.85 : 0.35,
                }}
                animate={halo}
                transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            />

            <div
                className="absolute -inset-6 rounded-full"
                style={{
                    background: isWarm
                        ? "radial-gradient(circle, rgba(255,154,120,0.5) 0%, rgba(255,154,120,0) 70%)"
                        : "radial-gradient(circle, rgba(91,194,204,0.5) 0%, rgba(184,232,232,0) 70%)",
                    filter: "blur(20px)",
                    opacity: state === "video" ? 0.3 : 0.9,
                }}
            />

            <motion.div
                className="absolute inset-0 rounded-full"
                style={{
                    background: isWarm
                        ? "radial-gradient(circle at 35% 30%, #FFFFFF 0%, #FFE6D5 25%, #FFB695 65%, #FF9A78 100%)"
                        : "radial-gradient(circle at 35% 30%, #FFFFFF 0%, #D2EEEE 22%, #7ACCD3 55%, #5BC2CC 100%)",
                    boxShadow: isWarm
                        ? "inset 0 -20px 40px rgba(255,154,120,0.4), inset 0 20px 40px rgba(255,255,255,0.65)"
                        : "inset 0 -20px 40px rgba(91,194,204,0.45), inset 0 20px 40px rgba(255,255,255,0.7)",
                }}
                animate={coreVariants[state]}
                transition={coreTransition[state]}
            />

            <div
                className="absolute rounded-full pointer-events-none"
                style={{
                    inset: "8%",
                    background:
                        "radial-gradient(ellipse at 30% 22%, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0) 45%)",
                    mixBlendMode: "screen",
                }}
            />

            {(state === "speaking" || state === "explaining") &&
                [...Array(6)].map((_, i) => {
                    const angle = (i / 6) * Math.PI * 2;
                    const r = dim * 0.55;
                    return (
                        <motion.div
                            key={i}
                            className="absolute rounded-full"
                            style={{
                                width: 6,
                                height: 6,
                                left: "50%",
                                top: "50%",
                                background: isWarm ? "#FF9A78" : "#5BC2CC",
                                marginLeft: -3,
                                marginTop: -3,
                                boxShadow: isWarm
                                    ? "0 0 12px rgba(255,154,120,0.9)"
                                    : "0 0 12px rgba(91,194,204,0.9)",
                            }}
                            animate={{
                                x: [0, Math.cos(angle) * r],
                                y: [0, Math.sin(angle) * r],
                                opacity: [0, 1, 0],
                                scale: [0.4, 1, 0.4],
                            }}
                            transition={{
                                duration: 2.6,
                                repeat: Infinity,
                                delay: i * 0.18,
                                ease: "easeOut",
                            }}
                        />
                    );
                })}
        </div>
    );
};

export default Mascot;
